import React, { useEffect, useState } from "react";
import {
  X,
  Play,
  RotateCcw,
  Sparkles,
  Loader2,
  MessageSquare,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import { simulationRunSimulation, agentsListModels } from "../gen";
import type {
  SimulationRequest,
  SimulationResponse,
  ModelCatalogResponse,
} from "../gen/models";
import { computeWordDiff } from "./simulator/simulatorDiffUtils";
import { SimulationResultView } from "./simulator/SimulationResultView";
import { InteractiveSandboxView, type ChatMessage } from "./simulator/InteractiveSandboxView";

import { useAgentOptions } from "../hooks/useAgentOptions";

export interface SimulatorDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  batchId?: string;
  batchTitle?: string;
  threadId?: string;
  targetTurnId?: string;
  defaultAgentType?: string;
  initialInquiry?: string;
  initialBaselineContent?: string;
}

export function SimulatorDrawer({
  isOpen,
  onClose,
  batchId,
  batchTitle,
  threadId,
  targetTurnId,
  defaultAgentType = "sales",
  initialInquiry = "",
  initialBaselineContent = "",
}: SimulatorDrawerProps) {
  const [mode, setMode] = useState<"single_turn_replay" | "interactive_sandbox">(
    "single_turn_replay"
  );
  const [agentType, setAgentType] = useState<string>(defaultAgentType);
  const agentOptions = useAgentOptions();
  const [inquiryText, setInquiryText] = useState<string>(initialInquiry);
  const [isSimulating, setIsSimulating] = useState(false);
  const [simResult, setSimResult] = useState<SimulationResponse | null>(null);
  const [showAuditing, setShowAuditing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Interactive sandbox state
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState("");

  const [selectedModel, setSelectedModel] = useState<string>("");
  const [modelCatalog, setModelCatalog] = useState<ModelCatalogResponse | null>(null);

  useEffect(() => {
    async function loadModels() {
      try {
        const resp = await agentsListModels();
        if ("data" in resp && resp.status === 200) {
          setModelCatalog(resp.data);
        }
      } catch (err) {
        console.warn("Could not load dynamic models catalog in SimulatorDrawer:", err);
      }
    }
    loadModels();
  }, []);

  useEffect(() => {
    if (isOpen) {
      if (initialInquiry) setInquiryText(initialInquiry);
      if (defaultAgentType) setAgentType(defaultAgentType);
      setError(null);
    }
  }, [isOpen, initialInquiry, defaultAgentType]);

  if (!isOpen) return null;

  const handleRunSingleTurnReplay = async () => {
    setIsSimulating(true);
    setError(null);

    const payload: SimulationRequest = {
      mode: "single_turn_replay",
      agent_type: agentType,
      thread_id: threadId,
      target_turn_id: targetTurnId,
      input_text: inquiryText || undefined,
      model_name: selectedModel || undefined,
      draft_overrides: batchId ? { batch_id: batchId } : undefined,
    };

    try {
      const resp = await simulationRunSimulation(payload);
      if ("data" in resp && resp.status === 200) {
        setSimResult(resp.data);
      } else {
        const errDetail = (resp as any)?.error?.detail || "Simulation execution failed";
        setError(typeof errDetail === "string" ? errDetail : JSON.stringify(errDetail));
      }
    } catch (err: any) {
      setError(err?.message || "Failed to contact simulation service");
    } finally {
      setIsSimulating(false);
    }
  };

  const handleSendInteractiveMessage = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const prompt = chatInput.trim();
    if (!prompt || isSimulating) return;

    const userMsg: ChatMessage = {
      id: "usr-" + Date.now(),
      role: "user",
      content: prompt,
    };
    setChatMessages((prev) => [...prev, userMsg]);
    setChatInput("");
    setIsSimulating(true);
    setError(null);

    const payload: SimulationRequest = {
      mode: "interactive_sandbox",
      agent_type: agentType,
      thread_id: threadId,
      input_text: prompt,
      model_name: selectedModel || undefined,
      draft_overrides: batchId ? { batch_id: batchId } : undefined,
    };

    try {
      const resp = await simulationRunSimulation(payload);
      if ("data" in resp && resp.status === 200) {
        setSimResult(resp.data);
        const assistantMsg: ChatMessage = {
          id: "asst-" + Date.now(),
          role: "assistant",
          content: resp.data.simulated_output.content,
          outputMeta: resp.data.simulated_output,
        };
        setChatMessages((prev) => [...prev, assistantMsg]);
      } else {
        const errDetail = (resp as any)?.error?.detail || "Interactive turn execution failed";
        setError(typeof errDetail === "string" ? errDetail : JSON.stringify(errDetail));
      }
    } catch (err: any) {
      setError(err?.message || "Failed to execute interactive simulation turn");
    } finally {
      setIsSimulating(false);
    }
  };

  const baselineText =
    simResult?.baseline_output?.content || initialBaselineContent || "";
  const simulatedText = simResult?.simulated_output?.content || "";
  const { oldTokens, newTokens } = computeWordDiff(baselineText, simulatedText);

  const tokenDiff = simResult?.delta_metrics?.token_diff ?? 0;
  const latencyDiff = simResult?.delta_metrics?.latency_diff_ms ?? 0;
  const injectedCount = (simResult?.rule_evaluation?.injected_draft_rules || []).length;
  const excludedCount = (simResult?.rule_evaluation?.superseded_rules_excluded || []).length;
  const toolsCalled = simResult?.simulated_output?.tools_called || [];
  const rulesApplied = simResult?.simulated_output?.rules_applied || [];
  const knowledgeUsed = simResult?.simulated_output?.knowledge_used || [];

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-stone-900/60 backdrop-blur-xs flex justify-end animate-in fade-in duration-200">
      <div className="w-full max-w-4xl bg-white dark:bg-stone-900 border-l border-stone-200 dark:border-stone-800 shadow-2xl flex flex-col h-full animate-in slide-in-from-right duration-300">
        {/* Drawer Header */}
        <div className="px-6 py-4 border-b border-stone-200 dark:border-stone-800 flex items-center justify-between bg-stone-50/75 dark:bg-stone-900/75">
          <div className="space-y-1">
            <div className="flex items-center gap-2.5">
              <div className="p-1.5 rounded-lg bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400">
                <Sparkles className="w-4 h-4" />
              </div>
              <h2 className="text-sm font-bold text-stone-900 dark:text-white">
                Pre-Merge Sandbox Simulation
              </h2>
              {batchTitle && (
                <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300">
                  Batch: {batchTitle}
                </span>
              )}
            </div>
            <p className="text-xs text-stone-500 dark:text-stone-400">
              Verify candidate draft rules and knowledge items against live tools in an isolated sandbox.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-stone-400 hover:text-stone-700 dark:hover:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-800 transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Configuration Bar */}
        <div className="px-6 py-3 border-b border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 flex flex-wrap items-center justify-between gap-3 text-xs">
          {/* Mode Selector */}
          <div className="flex items-center rounded-lg bg-stone-100 dark:bg-stone-800 p-0.5 border border-stone-200 dark:border-stone-700/60">
            <button
              type="button"
              onClick={() => setMode("single_turn_replay")}
              className={`flex items-center gap-1.5 px-3 py-1 rounded-md font-medium transition ${
                mode === "single_turn_replay"
                  ? "bg-white dark:bg-stone-900 text-stone-900 dark:text-white shadow-2xs"
                  : "text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200"
              }`}
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span>Single-Turn Replay</span>
            </button>
            <button
              type="button"
              onClick={() => setMode("interactive_sandbox")}
              className={`flex items-center gap-1.5 px-3 py-1 rounded-md font-medium transition ${
                mode === "interactive_sandbox"
                  ? "bg-white dark:bg-stone-900 text-stone-900 dark:text-white shadow-2xs"
                  : "text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200"
              }`}
            >
              <MessageSquare className="w-3.5 h-3.5" />
              <span>Interactive Sandbox</span>
            </button>
          </div>

          {/* Selectors Group */}
          <div className="flex flex-wrap items-center gap-4">
            {/* Agent Type Selector */}
            <div className="flex items-center gap-2">
              <label className="text-stone-400 font-medium">Target Agent:</label>
              <select
                value={agentType}
                onChange={(e) => setAgentType(e.target.value)}
                className="bg-stone-50 dark:bg-stone-800 border border-stone-300 dark:border-stone-700 text-stone-900 dark:text-stone-100 rounded-md px-2 py-1 text-xs focus:ring-1 focus:ring-indigo-500"
              >
                {agentOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Model Override Selector */}
            <div className="flex items-center gap-2">
              <label className="text-stone-400 font-medium">Model:</label>
              <select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                className="bg-stone-50 dark:bg-stone-800 border border-stone-300 dark:border-stone-700 text-stone-900 dark:text-stone-100 rounded-md px-2 py-1 text-xs focus:ring-1 focus:ring-indigo-500 font-mono"
              >
                <option value="">Default Configured Model</option>
                {modelCatalog?.curated_models && modelCatalog.curated_models.length > 0 && (
                  <optgroup label="Curated Models">
                    {modelCatalog.curated_models.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name} ({m.provider})
                      </option>
                    ))}
                  </optgroup>
                )}
                {modelCatalog?.models && modelCatalog.models.length > 0 && (
                  <optgroup label="All Models">
                    {modelCatalog.models.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name} ({m.id})
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
            </div>
          </div>
        </div>

        {/* Drawer Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5 [scrollbar-gutter:stable]">
          {error && (
            <div className="p-3 rounded-lg bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/60 text-xs text-rose-800 dark:text-rose-300 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {mode === "single_turn_replay" ? (
            /* Single-Turn Replay UI */
            <div className="space-y-5">
              {/* Inquiry Input Form */}
              <div className="p-4 rounded-xl border border-stone-200 dark:border-stone-800 bg-stone-50/50 dark:bg-stone-950/40 space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-stone-800 dark:text-stone-200 flex items-center gap-1.5">
                    <span>Visitor Inquiry / Counterfactual Message</span>
                  </label>
                  {threadId && (
                    <span className="text-[10px] text-stone-400">
                      Bound to Thread: {threadId.slice(0, 8)}...
                    </span>
                  )}
                </div>
                <textarea
                  value={inquiryText}
                  onChange={(e) => setInquiryText(e.target.value)}
                  placeholder="Enter visitor message to test with candidate draft rules..."
                  rows={2}
                  className="w-full text-xs p-2.5 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 text-stone-900 dark:text-stone-100 placeholder:text-stone-400 focus:outline-hidden focus:ring-2 focus:ring-indigo-500 font-sans"
                />
                <div className="flex items-center justify-between pt-1">
                  <div className="text-[11px] text-stone-500 flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                    <span>Isolated DB write (status='simulation')</span>
                  </div>
                  <button
                    type="button"
                    onClick={handleRunSingleTurnReplay}
                    disabled={isSimulating}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold transition shadow-xs disabled:opacity-50"
                  >
                    {isSimulating ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Play className="w-3.5 h-3.5 fill-current" />
                    )}
                    <span>{isSimulating ? "Simulating..." : "Run Pre-Merge Simulation"}</span>
                  </button>
                </div>
              </div>

              {/* Simulation Result Split-Pane & Diffs */}
              {simResult && (
                <SimulationResultView
                  simResult={simResult}
                  oldTokens={oldTokens}
                  newTokens={newTokens}
                  tokenDiff={tokenDiff}
                  latencyDiff={latencyDiff}
                  injectedCount={injectedCount}
                  excludedCount={excludedCount}
                  toolsCalled={toolsCalled}
                  rulesApplied={rulesApplied}
                  knowledgeUsed={knowledgeUsed}
                  showAuditing={showAuditing}
                  onToggleAuditing={() => setShowAuditing((prev) => !prev)}
                />
              )}
            </div>
          ) : (
            <InteractiveSandboxView
              chatMessages={chatMessages}
              chatInput={chatInput}
              onChangeChatInput={setChatInput}
              onSubmit={handleSendInteractiveMessage}
              isSimulating={isSimulating}
            />
          )}
        </div>
      </div>
    </div>
  );
}
